/** Mail search queries (2026-10-02): what "to:carolyn has:attachment
 *  received:last week" means, shared by the search box (suggestions, the
 *  list's title) and the daemon (which runs it over the header cache).
 *
 *  Grammar. Words are ANDed. A word may be an operator, `op:value` or
 *  `op: value` (a space after the colon is allowed, as people type it);
 *  values with spaces are quoted: `from:"Carolyn McDonald"`. A leading
 *  `-` negates a term: `-from:noreply`. Anything else is plain text,
 *  matched against sender, recipients and subject (and, in a second pass
 *  the daemon runs through Outlook, the message text).
 *
 *    from: sender:                 who sent it (name or address)
 *    to: cc: bcc:                  who it went to (names; an address is
 *                                  matched through the names it is known by)
 *    with: participants:           from, to or cc
 *    subject: about:               the subject
 *    has:attachment has:flag       has:attachment, also hasattachments:yes
 *    is:unread is:read is:flagged is:replied is:external is:internal
 *    is:meeting is:important is:slt is:extvip
 *    category: cat: label:         an Outlook category (the `_p_` prefix
 *                                  is optional)
 *    in: folder:                   inbox, sent, deleted, junk, drafts,
 *                                  archive, or any part of a folder path
 *    received: on: date: sent:     today, yesterday, this week, last week,
 *                                  this month, last month, this year, last
 *                                  year, a weekday, a month name, a date
 *    after: since:  before: until: a date or one of the words above
 *    newer_than: older_than:       7d, 2w, 3m, 1y
 *
 *  Weeks start on Sunday (Outlook's US default). Dates are calendar days;
 *  the caller turns them into instants in the mailbox's time zone. Pure. */
const PERSON_OPS = {
    from: "from",
    sender: "from",
    to: "to",
    recipient: "to",
    recipients: "to",
    cc: "cc",
    bcc: "bcc",
    with: "with",
    participant: "with",
    participants: "with",
    people: "with",
    involving: "with",
};
const OPS = {
    ...PERSON_OPS,
    subject: "subject",
    about: "subject",
    title: "subject",
    body: "text",
    text: "text",
    contains: "text",
    has: "has",
    is: "is",
    hasattachment: "hasattachment",
    hasattachments: "hasattachment",
    attachments: "hasattachment",
    category: "category",
    categories: "category",
    cat: "category",
    label: "category",
    tag: "category",
    in: "folder",
    folder: "folder",
    received: "on",
    sent: "on",
    on: "on",
    date: "on",
    when: "on",
    during: "on",
    after: "after",
    since: "after",
    before: "before",
    until: "before",
    newer_than: "newer",
    within: "newer",
    older_than: "older",
};
/** Operator names offered by the search box, in the order it lists them. */
export const SEARCH_OPERATORS = [
    { op: "from", hint: "who sent it", example: "from:carolyn" },
    { op: "to", hint: "who it was sent to", example: "to:ben" },
    { op: "cc", hint: "who was copied", example: "cc:dana" },
    { op: "with", hint: "from, to or cc", example: "with:carolyn" },
    { op: "subject", hint: "words in the subject", example: 'subject:"q4 plan"' },
    { op: "has", hint: "attachment, flag", example: "has:attachment" },
    { op: "is", hint: "unread, flagged, replied, slt, extvip…", example: "is:unread" },
    { op: "received", hint: "today, last week, a date…", example: 'received:"last week"' },
    { op: "before", hint: "before a date", example: "before:2026-09-01" },
    { op: "after", hint: "on or after a date", example: "after:2026-09-01" },
    { op: "in", hint: "a folder: inbox, sent, deleted…", example: "in:sent" },
    { op: "category", hint: "an Outlook category", example: "category:slt" },
];
export const IS_VALUES = [
    { value: "unread", flag: "unread", on: true, label: "Unread" },
    { value: "read", flag: "unread", on: false, label: "Read" },
    { value: "flagged", flag: "flagged", on: true, label: "Flagged" },
    { value: "unflagged", flag: "flagged", on: false, label: "Not flagged" },
    { value: "replied", flag: "replied", on: true, label: "Replied to" },
    { value: "unreplied", flag: "replied", on: false, label: "Not replied to" },
    { value: "external", flag: "external", on: true, label: "From outside" },
    { value: "internal", flag: "external", on: false, label: "From inside" },
    { value: "meeting", flag: "meeting", on: true, label: "Meeting invitations and replies" },
    { value: "important", flag: "important", on: true, label: "High importance" },
    { value: "slt", flag: "slt", on: true, label: "From SLT+" },
    { value: "extvip", flag: "extVip", on: true, label: "From External VIPs" },
    { value: "team", flag: "team", on: true, label: "From your team" },
];
const IS_ALIASES = {
    new: "unread",
    flag: "flagged",
    answered: "replied",
    invite: "meeting",
    invitation: "meeting",
    high: "important",
    urgent: "important",
    "slt+": "slt",
    leader: "slt",
    leaders: "slt",
    leadership: "slt",
    exec: "slt",
    vip: "extvip",
    "ext-vip": "extvip",
    externalvip: "extvip",
    outside: "external",
    inside: "internal",
    myteam: "team",
    reports: "team",
};
export const HAS_VALUES = [
    { value: "attachment", flag: "attachment", label: "Has attachments" },
    { value: "flag", flag: "flagged", label: "Flagged" },
];
const HAS_ALIASES = {
    attachments: "attachment",
    attach: "attachment",
    file: "attachment",
    files: "attachment",
    flags: "flag",
    flagged: "flag",
};
export const DATE_WORDS = ["today", "yesterday", "this week", "last week", "this month", "last month", "this year", "last year"];
export const FOLDER_ALIASES = {
    inbox: "Inbox",
    sent: "Sent Items",
    "sent items": "Sent Items",
    deleted: "Deleted Items",
    "deleted items": "Deleted Items",
    trash: "Deleted Items",
    bin: "Deleted Items",
    junk: "Junk Email",
    spam: "Junk Email",
    drafts: "Drafts",
    draft: "Drafts",
    archive: "Archive",
    outbox: "Outbox",
};
function isSpace(c) {
    return c !== undefined && /\s/.test(c);
}
/** Curly double quotes as straight ones (2026-10-09): macOS turns a typed
 *  " into “ or ”, and `from:"Waqas Ahmed” pulse` then read as one sender
 *  named "Waqas Ahmed” pulse". One character for one, so token positions
 *  still index the query as typed. */
export function normalizeQuotes(q) {
    return q.replace(/[\u201C\u201D\u201E\u201F\u2033\u00AB\u00BB]/g, '"');
}
/** Split a query into tokens: operators (known ones only, so "Re: budget"
 *  stays text), quoted phrases and words. */
export function tokenizeMailSearch(q) {
    q = normalizeQuotes(q);
    const out = [];
    let i = 0;
    const n = q.length;
    const readValue = () => {
        if (q[i] === '"') {
            const close = q.indexOf('"', i + 1);
            const value = close < 0 ? q.slice(i + 1) : q.slice(i + 1, close);
            i = close < 0 ? n : close + 1;
            return { value, quoted: true };
        }
        const s = i;
        while (i < n && !isSpace(q[i]))
            i++;
        return { value: q.slice(s, i), quoted: false };
    };
    while (i < n) {
        while (i < n && isSpace(q[i]))
            i++;
        if (i >= n)
            break;
        const start = i;
        let negate = false;
        if (q[i] === "-" && i + 1 < n && !isSpace(q[i + 1])) {
            negate = true;
            i++;
        }
        const m = /^([A-Za-z_]+):/.exec(q.slice(i));
        if (m && OPS[m[1].toLowerCase()]) {
            const op = m[1].toLowerCase();
            i += m[0].length;
            // "to: carolyn": the value may follow a space.
            let j = i;
            while (j < n && isSpace(q[j]))
                j++;
            const nextIsOp = /^-?[A-Za-z_]+:/.exec(q.slice(j));
            if (j < n && !(nextIsOp && OPS[nextIsOp[0].replace(/^-|:$/g, "").toLowerCase()]))
                i = j;
            let { value, quoted } = i < n && !isSpace(q[i]) ? readValue() : { value: "", quoted: false };
            // Date words of two: received:this week, received:last month.
            const kind = OPS[op];
            if (!quoted && (kind === "on" || kind === "after" || kind === "before") && /^(this|last|past)$/i.test(value)) {
                let k = i;
                while (k < n && isSpace(q[k]))
                    k++;
                const s = k;
                while (k < n && !isSpace(q[k]))
                    k++;
                if (k > s) {
                    value = `${value} ${q.slice(s, k)}`;
                    i = k;
                }
            }
            // A month and a year: received:sep 2026.
            if (!quoted && kind === "on" && MONTHS.findIndex((mo) => mo.startsWith(value.toLowerCase().slice(0, 3))) >= 0 && /^[a-z]{3,}$/i.test(value)) {
                const yr = /^\s+(\d{4})(?=\s|$)/.exec(q.slice(i));
                if (yr) {
                    value = `${value} ${yr[1]}`;
                    i += yr[0].length;
                }
            }
            out.push({ start, end: i, negate, op, value, quoted });
            continue;
        }
        const { value, quoted } = readValue();
        out.push({ start, end: i, negate, op: null, value, quoted });
    }
    return out;
}
const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
function toYmd(ms) {
    const d = new Date(ms);
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
}
function ms(ymd) {
    return Date.UTC(ymd.y, ymd.m - 1, ymd.d);
}
function add(ymd, days) {
    return toYmd(ms(ymd) + days * 86_400_000);
}
function str(ymd) {
    return `${ymd.y}-${String(ymd.m).padStart(2, "0")}-${String(ymd.d).padStart(2, "0")}`;
}
function parseIsoDay(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
}
function valid(ymd) {
    const back = toYmd(ms(ymd));
    return back.y === ymd.y && back.m === ymd.m && back.d === ymd.d;
}
function shortDay(ymd) {
    return `${MONTHS[ymd.m - 1].slice(0, 1).toUpperCase()}${MONTHS[ymd.m - 1].slice(1, 3)} ${ymd.d}`;
}
/** A date value as a span of days [start, end), with a label. `today` is
 *  "YYYY-MM-DD" in the mailbox's zone. */
export function parseDateSpan(value, today) {
    const t = parseIsoDay(today);
    if (!t)
        return null;
    // A range, both ends included: 2026-09-01..2026-09-30, "last week..today".
    const range = /^(.+?)\.\.(.+)$/.exec(value.trim());
    if (range) {
        const a = parseDateSpan(range[1], today);
        const b = parseDateSpan(range[2], today);
        if (!a || !b || b.end <= a.start)
            return null;
        return { start: a.start, end: b.end, label: `${a.label} – ${b.label}` };
    }
    const v = value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
    const span = (a, b, label) => ({ start: str(a), end: str(b), label });
    const dow = new Date(ms(t)).getUTCDay();
    const weekStart = add(t, -dow);
    const monthStart = { y: t.y, m: t.m, d: 1 };
    const nextMonth = (y, m) => (m === 12 ? { y: y + 1, m: 1, d: 1 } : { y, m: m + 1, d: 1 });
    switch (v) {
        case "today":
            return span(t, add(t, 1), "today");
        case "yesterday":
            return span(add(t, -1), t, "yesterday");
        case "this week":
            return span(weekStart, add(weekStart, 7), "this week");
        case "last week":
            return span(add(weekStart, -7), weekStart, "last week");
        case "past week":
            return span(add(t, -6), add(t, 1), "the past 7 days");
        case "this month":
            return span(monthStart, nextMonth(t.y, t.m), "this month");
        case "last month": {
            const lm = t.m === 1 ? { y: t.y - 1, m: 12, d: 1 } : { y: t.y, m: t.m - 1, d: 1 };
            return span(lm, monthStart, "last month");
        }
        case "past month":
            return span(add(t, -29), add(t, 1), "the past 30 days");
        case "this year":
            return span({ y: t.y, m: 1, d: 1 }, { y: t.y + 1, m: 1, d: 1 }, "this year");
        case "last year":
            return span({ y: t.y - 1, m: 1, d: 1 }, { y: t.y, m: 1, d: 1 }, "last year");
    }
    // A weekday: the latest one, today included.
    const wd = WEEKDAYS.findIndex((w) => w === v || (v.length >= 3 && w.startsWith(v)));
    if (wd >= 0 && /^[a-z]+$/.test(v)) {
        const back = (dow - wd + 7) % 7;
        const day = add(t, -back);
        return span(day, add(day, 1), `${WEEKDAYS[wd][0].toUpperCase()}${WEEKDAYS[wd].slice(1)} ${shortDay(day)}`);
    }
    // A month, with or without a year: the latest one not in the future.
    const mm = /^([a-z]{3,})(?: (\d{4}))?$/.exec(v);
    if (mm) {
        const mi = MONTHS.findIndex((m) => m.startsWith(mm[1]));
        if (mi >= 0) {
            const y = mm[2] ? +mm[2] : mi + 1 > t.m ? t.y - 1 : t.y;
            const a = { y, m: mi + 1, d: 1 };
            const name = MONTHS[mi][0].toUpperCase() + MONTHS[mi].slice(1);
            return span(a, nextMonth(y, mi + 1), `${name} ${y}`);
        }
    }
    // 2026-09-01, 2026/9/1
    let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(value.trim());
    if (m) {
        const day = { y: +m[1], m: +m[2], d: +m[3] };
        return valid(day) ? span(day, add(day, 1), shortDay(day) + (day.y !== t.y ? ` ${day.y}` : "")) : null;
    }
    // 9/1/2026, 9/1/26, 9/1 (the latest such day not in the future)
    m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/.exec(value.trim());
    if (m) {
        let y = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : t.y;
        let day = { y, m: +m[1], d: +m[2] };
        if (!m[3] && ms(day) > ms(t)) {
            y -= 1;
            day = { y, m: +m[1], d: +m[2] };
        }
        return valid(day) ? span(day, add(day, 1), shortDay(day) + (day.y !== t.y ? ` ${day.y}` : "")) : null;
    }
    return null;
}
/** "7d", "2w", "3m", "1y" back from today. */
function relativeStart(value, today) {
    const t = parseIsoDay(today);
    const m = /^(\d{1,4})\s*([dwmy])$/i.exec(value.trim());
    if (!t || !m)
        return null;
    const n = +m[1];
    const unit = m[2].toLowerCase();
    const days = unit === "d" ? n : unit === "w" ? n * 7 : unit === "m" ? n * 30 : n * 365;
    const word = unit === "d" ? "day" : unit === "w" ? "week" : unit === "m" ? "month" : "year";
    return { day: str(add(t, -days)), label: `${n} ${word}${n === 1 ? "" : "s"}` };
}
// ---------------------------------------------------------------------------
// Parse
export function parseMailSearch(q, today) {
    const out = { terms: [], flags: {}, after: null, before: null, dateLabel: null, warnings: [] };
    const labels = [];
    const narrow = (after, before) => {
        if (after && (!out.after || after > out.after))
            out.after = after;
        if (before && (!out.before || before < out.before))
            out.before = before;
    };
    for (const tok of tokenizeMailSearch(q)) {
        const value = tok.value.trim();
        if (!tok.op) {
            if (value && !/^(and)$/i.test(value))
                out.terms.push({ kind: "text", value, negate: tok.negate });
            continue;
        }
        const kind = OPS[tok.op];
        if (!value)
            continue; // "to:" still being typed
        const asText = () => {
            out.warnings.push(`${tok.op}:${value}`);
            out.terms.push({ kind: "text", value, negate: tok.negate });
        };
        switch (kind) {
            case "from":
            case "to":
            case "cc":
            case "bcc":
            case "with":
            case "subject":
            case "text":
            case "category":
            case "folder":
                out.terms.push({ kind, value, negate: tok.negate });
                break;
            case "has": {
                const v = HAS_ALIASES[value.toLowerCase()] ?? value.toLowerCase();
                const h = HAS_VALUES.find((x) => x.value === v);
                if (h)
                    out.flags[h.flag] = !tok.negate;
                else
                    asText();
                break;
            }
            case "hasattachment": {
                const v = value.toLowerCase();
                if (/^(yes|true|1|y)$/.test(v))
                    out.flags.attachment = !tok.negate;
                else if (/^(no|false|0|n)$/.test(v))
                    out.flags.attachment = tok.negate;
                else
                    asText();
                break;
            }
            case "is": {
                const raw = value.toLowerCase();
                const v = IS_ALIASES[raw] ?? raw;
                const f = IS_VALUES.find((x) => x.value === v);
                if (f)
                    out.flags[f.flag] = tok.negate ? !f.on : f.on;
                else
                    asText();
                break;
            }
            case "on":
            case "after":
            case "before": {
                const sp = parseDateSpan(value, today);
                if (!sp) {
                    asText();
                    break;
                }
                if (kind === "on") {
                    narrow(sp.start, sp.end);
                    labels.push(sp.label);
                }
                else if (kind === "after") {
                    narrow(sp.start, null);
                    labels.push(`since ${sp.label}`);
                }
                else {
                    narrow(null, sp.start);
                    labels.push(`before ${sp.label}`);
                }
                break;
            }
            case "newer":
            case "older": {
                const r = relativeStart(value, today);
                if (!r) {
                    asText();
                    break;
                }
                if (kind === "newer") {
                    narrow(r.day, null);
                    labels.push(`in the last ${r.label}`);
                }
                else {
                    narrow(null, r.day);
                    labels.push(`older than ${r.label}`);
                }
                break;
            }
        }
    }
    out.dateLabel = labels.length ? labels.join(", ") : null;
    return out;
}
/** True when the query asks for anything at all. */
export function isEmptySearch(p) {
    return p.terms.length === 0 && Object.keys(p.flags).length === 0 && !p.after && !p.before;
}
/** Plain-text terms that Outlook should also look for in the message text. */
export function textTermsOf(p) {
    return p.terms.filter((t) => (t.kind === "text") && !t.negate).map((t) => t.value);
}
const lc = (s) => (s ?? "").toLowerCase();
const words = (s) => s.toLowerCase().split(/[\s,;<>()"]+/).filter(Boolean);
/** One recipient entry ("McDonald, Carolyn" or "Carolyn McDonald <c@x>")
 *  matches a value when every word of the value is in it. */
function entryMatches(entry, value) {
    const e = entry.toLowerCase();
    const ws = words(value);
    return ws.length > 0 && ws.every((w) => e.includes(w));
}
function recipients(field) {
    return (field ?? "").split(";").map((s) => s.trim()).filter(Boolean);
}
function personMatches(row, field, value, opts) {
    const candidates = [value, ...(opts.alternativesFor?.(value) ?? [])];
    const entries = [];
    const fromEntry = `${row.from ?? ""} <${row.fromAddress ?? ""}>`;
    if (field === "from" || field === "with")
        entries.push(fromEntry);
    if (field === "to" || field === "with")
        entries.push(...recipients(row.to));
    if (field === "cc" || field === "with")
        entries.push(...recipients(row.cc));
    if (field === "bcc")
        entries.push(...recipients(row.bcc));
    return entries.some((e) => candidates.some((c) => entryMatches(e, c)));
}
function categoryNames(row) {
    return (row.categories ?? "").split(",").map((c) => c.trim().toLowerCase()).filter(Boolean);
}
/** The folder a value names: an alias's exact path, else any path that
 *  contains it. */
export function folderMatches(path, value) {
    const p = lc(path);
    const v = value.trim().toLowerCase();
    const alias = FOLDER_ALIASES[v];
    if (alias)
        return p === alias.toLowerCase();
    return v.length > 0 && p.includes(v);
}
/** Folders a search leaves out unless `in:` names them, as Outlook's
 *  "All mailboxes" search does. */
export function excludedByDefault(path) {
    return /^(deleted items|junk e-?mail|drafts|outbox|conversation history|rss (feeds|subscriptions)|sync issues(\/.*)?|conflicts|local failures|server failures)$/i.test((path ?? "").trim());
}
function termMatches(row, t, opts) {
    const v = t.value.toLowerCase();
    switch (t.kind) {
        case "from":
        case "to":
        case "cc":
        case "bcc":
        case "with":
            return personMatches(row, t.kind, t.value, opts);
        case "subject":
            return lc(row.subject).includes(v);
        case "category": {
            const want = v.replace(/^_p_/, "");
            return categoryNames(row).some((c) => c.includes(v) || c.replace(/^_p_/, "").includes(want));
        }
        case "folder":
            return folderMatches(row.folderPath, t.value);
        case "text": {
            if (opts.textMatchedElsewhere && !t.negate)
                return true;
            const hay = [row.subject, row.from, row.fromAddress, row.to, row.cc].map(lc).join("\n");
            return hay.includes(v);
        }
    }
}
function flagValue(row, f) {
    switch (f) {
        case "unread":
            return row.isRead === false;
        case "flagged":
            return lc(row.flagStatus) === "flagged";
        case "attachment":
            return !!row.hasAttachments;
        case "replied":
            return !!row.hasReplied || !!row.hasRepliedAll;
        case "external":
            return !!row.isExternal;
        case "meeting":
            return !!row.isMeeting;
        case "important":
            return row.importance === 2;
        case "slt":
            return /_p_slt_plus/i.test(row.categories ?? "");
        case "extVip":
            return /_p_external_vip/i.test(row.categories ?? "");
        case "team":
            return /_p_team_members/i.test(row.categories ?? "");
    }
}
/** Whether a row meets every term and flag (dates are the caller's: they
 *  need the mailbox's time zone). */
export function matchesMailSearch(row, p, opts = {}) {
    for (const t of p.terms)
        if (termMatches(row, t, opts) === t.negate)
            return false;
    for (const [f, want] of Object.entries(p.flags))
        if (flagValue(row, f) !== want)
            return false;
    if (!p.terms.some((t) => t.kind === "folder" && !t.negate) && excludedByDefault(row.folderPath))
        return false;
    return true;
}
// ---------------------------------------------------------------------------
// Words
/** A term as it is typed back into the box: `to:"Carolyn McDonald"`. */
export function formatSearchTerm(op, value, negate = false) {
    const v = /[\s"]/.test(value) ? `"${value.replace(/"/g, "")}"` : value;
    return `${negate ? "-" : ""}${op}:${v}`;
}
const KIND_WORDS = {
    from: ["From", "Not from"],
    to: ["Sent to", "Not sent to"],
    cc: ["Cc", "Not cc"],
    bcc: ["Bcc", "Not bcc"],
    with: ["With", "Not with"],
    subject: ["Subject has", "Subject without"],
    text: ["", "Without"],
    category: ["Category", "Not category"],
    folder: ["In", "Not in"],
};
/** What the search is, in words, for the list's title:
 *  `Sent to Carolyn McDonald · with attachments · last week`. */
export function describeMailSearch(p) {
    const parts = [];
    for (const t of p.terms) {
        const [pos, neg] = KIND_WORDS[t.kind];
        const lead = t.negate ? neg : pos;
        const val = t.kind === "subject" || t.kind === "text"
            ? `“${t.value}”`
            : t.kind === "folder"
                ? FOLDER_ALIASES[t.value.toLowerCase()] ?? t.value
                : t.value.toLowerCase() === "manager"
                    ? "your manager" // from:manager, resolved by the daemon to the owner's manager
                    : t.value;
        parts.push(lead ? `${lead} ${val}` : val);
    }
    for (const [f, on] of Object.entries(p.flags)) {
        const words = {
            unread: ["unread", "read"],
            flagged: ["flagged", "not flagged"],
            attachment: ["with attachments", "without attachments"],
            replied: ["replied to", "not replied to"],
            external: ["from outside", "from inside"],
            meeting: ["meetings", "not meetings"],
            important: ["high importance", "not high importance"],
            slt: ["from SLT+", "not from SLT+"],
            extVip: ["from External VIPs", "not from External VIPs"],
            team: ["from your team", "not from your team"],
        };
        parts.push(words[f][on ? 0 : 1]);
    }
    if (p.dateLabel)
        parts.push(p.dateLabel);
    const s = parts.join(" · ");
    return s ? s[0].toUpperCase() + s.slice(1) : "";
}
// ---------------------------------------------------------------------------
// Plain English (2026-10-02): "from carolyn last week about budget with
// attachments" → from:carolyn received:"last week" budget has:attachment.
const NOISE = new Set([
    "email", "emails", "mail", "mails", "message", "messages", "show", "find", "get", "list", "all", "any", "the", "a", "an",
    "that", "which", "were", "was", "are", "is", "have", "has", "had", "for", "please", "search", "thread", "threads",
    "conversation", "conversations", "my", "of", "where", "i", "got", "received",
    "sent", "wrote", "written", "emailed", "mailed",
]);
const NAME_STOP = new Set([
    "about", "regarding", "re", "with", "from", "to", "cc", "in", "on", "since", "before", "after", "last", "this", "past",
    "today", "yesterday", "that", "which", "and", "or", "sent", "subject", "titled", "unread", "flagged", "attachment",
    "attachments", "attached", "files", "older", "between", "by", "during", "until",
]);
/** Words that follow "to" in a sentence and are not people ("to do list"). */
const NOT_A_NAME = new Set(["do", "be", "go", "get", "see", "review", "approve", "sign", "discuss", "meet", "check", "read", "reply", "send", "me"]);
const FLAG_WORDS = [
    [["from", "my", "team"], "is:team"],
    [["my", "team"], "is:team"],
    [["my", "reports"], "is:team"],
    [["never", "answered"], "is:unreplied"],
    [["never", "replied"], "is:unreplied"],
    [["not", "answered"], "is:unreplied"],
    [["haven't", "answered"], "is:unreplied"],
    [["haven't", "replied"], "is:unreplied"],
    [["havent", "answered"], "is:unreplied"],
    [["havent", "replied"], "is:unreplied"],
    [["didn't", "answer"], "is:unreplied"],
    [["didn't", "reply"], "is:unreplied"],
    [["unread"], "is:unread"],
    [["flagged"], "is:flagged"],
    [["important"], "is:important"],
    [["urgent"], "is:important"],
    [["high", "priority"], "is:important"],
    [["high", "importance"], "is:important"],
    [["unanswered"], "is:unreplied"],
    [["unreplied"], "is:unreplied"],
    [["not", "replied"], "is:unreplied"],
    [["no", "reply"], "is:unreplied"],
    [["replied"], "is:replied"],
    [["from", "outside"], "is:external"],
    [["external", "vips"], "is:extvip"],
    [["external", "vip"], "is:extvip"],
    [["vips"], "is:extvip"],
    [["vip"], "is:extvip"],
    [["external"], "is:external"],
    [["internal"], "is:internal"],
    [["from", "slt"], "is:slt"],
    [["slt"], "is:slt"],
    [["slt+"], "is:slt"],
    [["leadership"], "is:slt"],
    [["executives"], "is:slt"],
    [["execs"], "is:slt"],
    [["meeting", "invites"], "is:meeting"],
    [["meeting", "requests"], "is:meeting"],
    [["invites"], "is:meeting"],
    [["invitations"], "is:meeting"],
];
const ATTACH_WORDS = new Set(["attachment", "attachments", "attached", "files"]);
/** "my boss", "my manager": the owner's manager (the daemon knows who). */
const MANAGER_NOUNS = new Set(["boss", "manager", "supervisor"]);
const FOLDER_WORDS = [
    [["sent", "items"], "sent"],
    [["sent"], "sent"],
    [["deleted", "items"], "deleted"],
    [["deleted"], "deleted"],
    [["trash"], "deleted"],
    [["junk"], "junk"],
    [["spam"], "junk"],
    [["inbox"], "inbox"],
    [["archive"], "archive"],
    [["drafts"], "drafts"],
];
const UNIT_DAYS = { day: "d", days: "d", week: "w", weeks: "w", month: "m", months: "m", year: "y", years: "y" };
/** A query written as a sentence, in operators; null when it already uses
 *  operators or has nothing a sentence would add (plain words stay plain). */
export function interpretNaturalSearch(q, today) {
    const toks = tokenizeMailSearch(q);
    if (toks.length === 0 || toks.some((t) => t.op || t.negate))
        return null;
    const words = toks.map((t) => ({ raw: t.value, lc: t.value.toLowerCase(), quoted: t.quoted }));
    const out = [];
    const text = [];
    let understood = false;
    let i = 0;
    const at = (k) => words[k]?.lc ?? "";
    const isDateStart = (k) => !!dateAt(k);
    // A date phrase at k: [value, length].
    function dateAt(k) {
        const w0 = at(k), w1 = at(k + 1), w2 = at(k + 2);
        if (w0 === "today" || w0 === "yesterday")
            return [w0, 1];
        if ((w0 === "this" || w0 === "last" || w0 === "past") && ["week", "month", "year"].includes(w1))
            return [`${w0} ${w1}`, 2];
        if ((w0 === "last" || w0 === "past") && /^\d+$/.test(w1) && UNIT_DAYS[w2])
            return [`newer:${w1}${UNIT_DAYS[w2]}`, 3];
        if (w0 === "in" && w1 === "the" && (w2 === "last" || w2 === "past") && /^\d+$/.test(at(k + 3)) && UNIT_DAYS[at(k + 4)])
            return [`newer:${at(k + 3)}${UNIT_DAYS[at(k + 4)]}`, 5];
        if (WEEKDAYS.some((d) => d === w0))
            return [w0, 1];
        return null;
    }
    // A person after from/to/cc/with: a quoted value, "me", or up to three
    // words that are not keywords.
    function personAt(k) {
        const w = words[k];
        if (!w)
            return null;
        if (w.quoted)
            return [w.raw, 1];
        if (w.lc === "me" || w.lc === "myself")
            return ["me", 1];
        if (w.lc === "my" && MANAGER_NOUNS.has(words[k + 1]?.lc ?? ""))
            return ["manager", 2];
        if (MANAGER_NOUNS.has(w.lc))
            return ["manager", 1];
        if (NAME_STOP.has(w.lc) || NOISE.has(w.lc) || NOT_A_NAME.has(w.lc) || isDateStart(k) || ATTACH_WORDS.has(w.lc))
            return null;
        const parts = [];
        let n = 0;
        while (n < 3 && words[k + n] && !words[k + n].quoted) {
            const lc = words[k + n].lc;
            if (NAME_STOP.has(lc) || NOISE.has(lc) || isDateStart(k + n) || ATTACH_WORDS.has(lc))
                break;
            parts.push(words[k + n].raw);
            n++;
        }
        // "with spreadsheets attached": a thing attached, not a person.
        if (ATTACH_WORDS.has(at(k + n)))
            return null;
        return parts.length ? [parts.join(" "), n] : null;
    }
    const term = (op, v) => (v === "me" ? `${op}:me` : formatSearchTerm(op, v));
    while (i < words.length) {
        const w = at(i);
        // "my boss ...": the owner's manager, as the sender unless a "to" or
        // "with" before it said otherwise (handled with the person rules).
        if (w === "my" && MANAGER_NOUNS.has(at(i + 1))) {
            out.push("from:manager");
            understood = true;
            i += 2;
            continue;
        }
        // "show me", "find me": asking, not a person.
        if ((w === "show" || w === "find" || w === "get" || w === "give") && at(i + 1) === "me") {
            i += 2;
            continue;
        }
        // Phrases of several words first.
        if ((w === "sent" && at(i + 1) === "by" && at(i + 2) === "me") || (w === "i" && at(i + 1) === "sent")) {
            out.push("from:me");
            understood = true;
            i += w === "i" ? 2 : 3;
            continue;
        }
        if ((w === "sent" || w === "addressed") && at(i + 1) === "to") {
            const p = personAt(i + 2);
            if (p) {
                out.push(term("to", p[0]));
                understood = true;
                i += 2 + p[1];
                continue;
            }
        }
        if (w === "sent" && at(i + 1) === "by") {
            const p = personAt(i + 2);
            if (p) {
                out.push(term("from", p[0]));
                understood = true;
                i += 2 + p[1];
                continue;
            }
        }
        if ((w === "copied" || w === "cc" || w === "cc'd" || w === "ccd") && (at(i + 1) === "to" || at(i + 1) === "on")) {
            const p = personAt(i + 2);
            if (p) {
                out.push(term("cc", p[0]));
                understood = true;
                i += 2 + p[1];
                continue;
            }
        }
        const flag = FLAG_WORDS.find(([ws]) => ws.every((x, j) => at(i + j) === x));
        if (flag) {
            out.push(flag[1]);
            understood = true;
            i += flag[0].length;
            continue;
        }
        if ((w === "with" || w === "has" || w === "have" || w === "having") && ATTACH_WORDS.has(at(i + 1))) {
            out.push("has:attachment");
            understood = true;
            i += 2;
            continue;
        }
        if (ATTACH_WORDS.has(w)) {
            out.push("has:attachment");
            understood = true;
            i += 1;
            continue;
        }
        if (w === "in" || w === "inside") {
            const f = FOLDER_WORDS.find(([ws]) => ws.every((x, j) => at(i + 1 + j) === x) || (at(i + 1) === "my" && ws.every((x, j) => at(i + 2 + j) === x)));
            if (f) {
                out.push(`in:${f[1]}`);
                understood = true;
                i += 1 + (at(i + 1) === "my" ? 1 : 0) + f[0].length;
                continue;
            }
            // "in september", "in sep 2025"
            const m = MONTHS.findIndex((mo) => at(i + 1).length >= 3 && mo.startsWith(at(i + 1)));
            if (m >= 0) {
                const yr = /^\d{4}$/.test(at(i + 2)) ? ` ${at(i + 2)}` : "";
                out.push(formatSearchTerm("received", MONTHS[m] + yr));
                understood = true;
                i += yr ? 3 : 2;
                continue;
            }
        }
        if (w === "since" || w === "after" || w === "before" || w === "until" || w === "on" || w === "during") {
            const d = dateAt(i + 1);
            const op = w === "since" || w === "after" ? "after" : w === "before" || w === "until" ? "before" : "received";
            if (d && !d[0].startsWith("newer:")) {
                out.push(formatSearchTerm(op, d[0]));
                understood = true;
                i += 1 + d[1];
                continue;
            }
            const raw = words[i + 1]?.raw ?? "";
            if (raw && parseDateSpan(raw, today)) {
                out.push(formatSearchTerm(op, raw));
                understood = true;
                i += 2;
                continue;
            }
        }
        if (w === "older" && at(i + 1) === "than" && /^\d+$/.test(at(i + 2)) && UNIT_DAYS[at(i + 3)]) {
            out.push(`older_than:${at(i + 2)}${UNIT_DAYS[at(i + 3)]}`);
            understood = true;
            i += 4;
            continue;
        }
        const d = dateAt(i);
        if (d) {
            out.push(d[0].startsWith("newer:") ? `newer_than:${d[0].slice(6)}` : formatSearchTerm("received", d[0]));
            understood = true;
            i += d[1];
            continue;
        }
        if (w === "from" || w === "by" || w === "to" || w === "with" || w === "involving" || w === "between" || w === "cc") {
            let k = i + 1;
            // "between me and carolyn" → with:carolyn
            if (w === "between" && at(k) === "me" && at(k + 1) === "and")
                k += 2;
            const p = personAt(k);
            if (p) {
                const op = w === "from" || w === "by" ? "from" : w === "to" ? "to" : w === "cc" ? "cc" : "with";
                out.push(term(op, p[0]));
                understood = true;
                i = k + p[1];
                continue;
            }
        }
        if ((w === "about" || w === "regarding" || w === "re") && words[i + 1]) {
            i += 1;
            continue; // the topic words that follow stay as words
        }
        if ((w === "subject" || w === "titled" || w === "called") && words[i + 1]) {
            const parts = [];
            let k = i + 1;
            if (words[k]?.quoted) {
                parts.push(words[k].raw);
                k++;
            }
            else {
                while (words[k] && !NAME_STOP.has(at(k)) && !isDateStart(k))
                    parts.push(words[k++].raw);
            }
            if (parts.length) {
                out.push(formatSearchTerm("subject", parts.join(" ")));
                understood = true;
                i = k;
                continue;
            }
        }
        if (NOISE.has(w) && !words[i].quoted) {
            i += 1;
            continue;
        }
        // "with spreadsheets attached": the "with" joins, it is not searched for.
        if (w === "with" && !words[i].quoted && ATTACH_WORDS.has(at(i + 2))) {
            i += 1;
            continue;
        }
        text.push(words[i].quoted ? `"${words[i].raw}"` : words[i].raw);
        i += 1;
    }
    if (!understood)
        return null;
    const query = [...out, ...text].join(" ");
    return { query, description: describeMailSearch(parseMailSearch(query, today)) };
}
export function searchChips(q, today) {
    const out = [];
    for (const tok of tokenizeMailSearch(q)) {
        const text = q.slice(tok.start, tok.end).trim();
        if (!text || (tok.op && !tok.value))
            continue;
        const p = parseMailSearch(text, today);
        const label = describeMailSearch(p) || text;
        const t = p.terms[0];
        const kind = p.after || p.before ? "date"
            : Object.keys(p.flags).length ? "flag"
                : !t ? "text"
                    : t.kind === "text" ? "text"
                        : t.kind === "subject" ? "subject"
                            : t.kind === "folder" ? "folder"
                                : t.kind === "category" ? "category"
                                    : "person";
        out.push({ start: tok.start, end: tok.end, text, label, kind });
    }
    return out;
}
/** The query without one chip. */
export function removeChip(q, chip) {
    return (q.slice(0, chip.start) + " " + q.slice(chip.end)).replace(/\s+/g, " ").trim();
}
/** The query with every date term replaced by `term` (or removed). */
export function replaceDates(q, today, term) {
    const keep = searchChips(q, today).filter((c) => c.kind !== "date").map((c) => c.text);
    return [...keep, ...(term ? [term] : [])].join(" ");
}
//# sourceMappingURL=mail-search.js.map